import React from "react";
import { NextRouter, withRouter } from "next/router";
import classNames from "classnames";
import Image from "next/image";

import { FilledButton, TextButton } from "components/common/Button/Button";
import Filter from "components/common/Inputs/Filter/Filter";
import {
  accessToTheCustomerOptions,
  genderOptions,
  paymentTypeOptions,
  priceOptions,
  PRICE_SEPARATOR_URL,
} from "constants/filterOptions";
import classes from "./Filters.module.css";
import {
  decodeParams,
  decodePrice,
  encodeValues,
} from "utils/functions/encodeDecodeURI";
import AdditionalFiltersModal from "./AdditionalFiltersModal/AdditionalFiltersModal";
import FilterIcon from "static/icons/filter.svg";
import MobileFiltersMenu from "./MobileFiltersMenu/MobileFiltersMenu";
import { massageSubcategories } from "constants/massageSubcategories";
import withTranslation from "next-translate/withTranslation";
import PriceFilter from "../../../common/Inputs/PriceFilter/PriceFilter";

export type AnnouncementFiltersKey =
  | "payment"
  | "genders"
  | "accessToTheCustomer"
  | "price"
  | "clientTypes"
  | "typeOfSalons"
  | "facilitateAccess";

export interface AnnouncementFilters {
  payment?: string[];
  genders?: string[];
  accessToTheCustomer?: string[];
  price?: string[];
  clientTypes?: string[];
  typeOfSalons?: string[];
  facilitateAccess?: string[];
}

type FiltersProps = {
  router: NextRouter;
  expanded?: boolean;
  moved?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  i18n: any;
};

type FiltersState = {
  filters: AnnouncementFilters;
  categoriesFilter: {
    [key: string]: string | string[] | { [key: string]: string | string[] };
  };
  mobileFiltersMenuVisible: boolean;
  additionalFiltersVisible: boolean;
};

class Filters extends React.Component<FiltersProps, FiltersState> {
  constructor(props: FiltersProps) {
    super(props);
    this.state = {
      filters: {
        payment: [],
        genders: [],
        accessToTheCustomer: [],
        price: [],
        clientTypes: [],
        typeOfSalons: [],
        facilitateAccess: [],
      },
      categoriesFilter: {
        mainCategories: [],
        subCategories: {},
      },
      mobileFiltersMenuVisible: false,
      additionalFiltersVisible: false,
    };
  }

  componentDidUpdate(prevProps: FiltersProps) {
    const { query: newQuery } = this.props.router;
    const { query: oldQuery } = prevProps.router;

    if (
      newQuery.category !== oldQuery.category ||
      (newQuery !== oldQuery && newQuery.category === oldQuery.category) ||
      (this.state.categoriesFilter.mainCategories.length === 0 &&
        newQuery.category)
    ) {
      newQuery.category
        ? this.extractCategoriesValuesFromURL(newQuery.category)
        : this.setState({
            categoriesFilter: { mainCategories: [], subCategories: {} },
          });
    }

    if (
      newQuery.payment !== oldQuery.payment ||
      newQuery.accessToTheCustomer !== oldQuery.accessToTheCustomer ||
      newQuery.genders !== oldQuery.genders ||
      newQuery.price !== oldQuery.price ||
      newQuery.clientTypes !== oldQuery.clientTypes ||
      newQuery.typeOfSalons !== oldQuery.typeOfSalons ||
      newQuery.facilitateAccess !== oldQuery.facilitateAccess
    ) {
      const {
        payment,
        genders,
        clientTypes,
        typeOfSalons,
        facilitateAccess,
        accessToTheCustomer,
        price,
      } = newQuery;

      const decodedPayment = payment && decodeParams(payment);
      const decodedGenders = genders && decodeParams(genders);
      const decodedClientTypes = clientTypes && decodeParams(clientTypes);
      const decodedTypeOfSalons = typeOfSalons && decodeParams(typeOfSalons);
      const decodedFacilitateAccess =
        facilitateAccess && decodeParams(facilitateAccess);

      const filtersValues = {
        ...(accessToTheCustomer
          ? { accessToTheCustomer: [decodeURI(accessToTheCustomer as string)] }
          : { accessToTheCustomer: [] }),
        ...(decodedPayment
          ? { payment: decodedPayment as string[] }
          : { payment: [] }),
        ...(decodedGenders
          ? { genders: decodedGenders as string[] }
          : { genders: [] }),
        ...(price && { price: decodePrice(decodeURI(price as string)) }),
        ...(decodedClientTypes
          ? { clientTypes: decodedClientTypes as string[] }
          : { clientTypes: [] }),
        ...(decodedTypeOfSalons
          ? { typeOfSalons: decodedTypeOfSalons as string[] }
          : { typeOfSalons: [] }),
        ...(decodedFacilitateAccess
          ? { facilitateAccess: decodedFacilitateAccess as string[] }
          : { facilitateAccess: [] }),
      };

      this.setState({ filters: filtersValues });
    }
  }

  extractCategoriesValuesFromURL = (decodecategory: string[] | string) => {
    const mainCategories: string[] = [];
    const subCategories: { [key: string]: string[] } = {};
    const { query } = this.props.router;

    if (decodecategory instanceof Array) {
      decodecategory.forEach((category) => {
        if (query[category]) {
          const subCategoriesValues = decodeParams(
            query[category] as string[]
          ) as string[];
          mainCategories.push(decodeURI(category));
          subCategories[decodeURI(category)] = subCategoriesValues;
        } else {
          mainCategories.push(decodeURI(category));
        }
      });
    } else if (query[query.category as string]) {
      const subCategoriesValues = decodeParams(
        query[query.category as string] as string[] | string
      );
      mainCategories.push(decodeURI(query.category as string));
      // @ts-ignore
      subCategories[decodeURI(query.category as string[])] =
        subCategoriesValues;
    } else {
      const decodedCategory = decodeURI(query.category as string);
      mainCategories.push(decodedCategory);
      massageSubcategories[decodedCategory] &&
        (subCategories[decodedCategory] =
          massageSubcategories[decodedCategory]);
    }

    this.setState({ categoriesFilter: { mainCategories, subCategories } });
  };

  checkboxFilterValueChangeHandler = (
    filterName: AnnouncementFiltersKey,
    value: string
  ) => {
    const currentValues = [...(this.state.filters[filterName] as string[])];
    if (!currentValues.includes(value)) {
      currentValues.push(value);
    } else {
      currentValues.splice(currentValues.indexOf(value), 1);
    }
    this.setState({
      filters: { ...this.state.filters, [filterName]: currentValues },
    });
  };

  radioButtonFilterValueChangeHandler = (filterName: string, value: string) => {
    this.setState({ filters: { ...this.state.filters, [filterName]: value } });
  };

  priceFilterValueChangeHandler = (value: string[]) => {
    this.setState({ filters: { ...this.state.filters, price: value } });
  };

  categoryMainFilterChangeHandler = (category: string) => {
    const currentMainCategories = [
      ...(this.state.categoriesFilter.mainCategories as string[]),
    ];
    const currentSubCategories = this.state.categoriesFilter.subCategories as {
      [key: string]: string[];
    };

    if (!currentMainCategories.includes(category)) {
      currentMainCategories.push(category);
      massageSubcategories[category] &&
        (currentSubCategories[category] = massageSubcategories[category]);
    } else {
      currentMainCategories.splice(currentMainCategories.indexOf(category), 1);
      currentSubCategories[category] = [];
    }

    this.setState({
      categoriesFilter: {
        mainCategories: currentMainCategories,
        subCategories: currentSubCategories,
      },
    });
  };

  categorySubFilterChangeHandler = (
    mainCategory: string,
    subCategory: string
  ) => {
    const currentMainCategories = [
      ...(this.state.categoriesFilter.mainCategories as string[]),
    ];
    const currentSubCategories = this.state.categoriesFilter.subCategories as {
      [key: string]: string[];
    };

    const currentValues = currentSubCategories[mainCategory]
      ? [...currentSubCategories[mainCategory]]
      : [];

    if (!currentValues.includes(subCategory)) {
      currentValues.push(subCategory);
      !currentMainCategories.includes(mainCategory) &&
        currentMainCategories.push(mainCategory);
    } else {
      currentValues.splice(currentValues.indexOf(subCategory), 1);

      currentValues.length === 0 &&
        currentMainCategories.splice(
          currentMainCategories.indexOf(mainCategory),
          1
        );
    }
    this.setState({
      categoriesFilter: {
        mainCategories: currentMainCategories,
        subCategories: {
          ...currentSubCategories,
          [mainCategory]: currentValues,
        },
      },
    });
  };

  submissionFiltersHandler = () => {
    const filtersValues = {
      ...this.state.filters,
      category: this.state.categoriesFilter.mainCategories as string[],
      ...(this.state.categoriesFilter.subCategories as {
        [key: string]: string[];
      }),
    };
    const encodedFiltersValues = encodeValues(filtersValues);
    this.props.router.push(
      {
        pathname: this.props.router.pathname,
        query: {
          ...this.props.router.query,
          ...encodedFiltersValues,
          price: (encodedFiltersValues.price as string[]).join(
            PRICE_SEPARATOR_URL
          ),
        },
      },
      undefined,
      { shallow: true }
    );
  };

  submissionMobileFiltersHandler = () => {
    this.submissionFiltersHandler();
    this.setState({ mobileFiltersMenuVisible: false });
  };

  clearFiltersHandler = () => {
    const clearedFiltersValues = { ...this.state.filters };
    Object.keys(clearedFiltersValues).forEach((key) => {
      clearedFiltersValues[key as AnnouncementFiltersKey] =
        (clearedFiltersValues[key as AnnouncementFiltersKey] instanceof Array &&
          []) ||
        [];
    });

    const clearedSubCategoriesValues = {
      ...(this.state.categoriesFilter.subCategories as {
        [key: string]: string[];
      }),
    };
    Object.keys(clearedSubCategoriesValues).forEach((key) => {
      clearedSubCategoriesValues[key] = [];
    });

    const clearedCategoriesFilter = {
      mainCategories: [],
      subCategories: clearedSubCategoriesValues,
    };

    this.setState({
      filters: clearedFiltersValues,
      categoriesFilter: clearedCategoriesFilter,
    });

    const filtersValues = {
      ...clearedFiltersValues,
      category: clearedCategoriesFilter.mainCategories as string[],
      ...(clearedCategoriesFilter.subCategories as { [key: string]: string[] }),
    };

    const encodedFilterValues = encodeValues(filtersValues);

    this.props.router.push(
      {
        pathname: this.props.router.pathname,
        query: {
          ...this.props.router.query,
          ...encodedFilterValues,
        },
      },
      undefined,
      { shallow: true }
    );
  };

  clearMobileFiltersHandler = () => {
    this.clearFiltersHandler();
    this.setState({ mobileFiltersMenuVisible: false });
  };

  clearFilterValuesHandler = (filterName: AnnouncementFiltersKey) => {
    const clearedFiltersValues = {
      ...this.state.filters,
      [filterName]:
        (this.state.filters[filterName] instanceof Array && []) || "",
    };
    this.setState({ filters: clearedFiltersValues });

    const encodedFiltersValues = encodeValues(clearedFiltersValues);
    this.props.router.push(
      {
        pathname: this.props.router.pathname,
        query: {
          ...this.props.router.query,
          ...encodedFiltersValues,
        },
      },
      undefined,
      { shallow: true }
    );
  };

  clearAdditionalFiltersHanlder = () => {
    const clearedFiltersValues = {
      ...this.state.filters,
      clientTypes: [],
      typeOfSalons: [],
      facilitateAccess: [],
    };
    this.setState({ filters: clearedFiltersValues });

    const encodedFiltersValues = encodeValues(clearedFiltersValues);
    this.props.router.push(
      {
        pathname: this.props.router.pathname,
        query: {
          ...this.props.router.query,
          ...encodedFiltersValues,
        },
      },
      undefined,
      { shallow: true }
    );
  };

  onOpenAdditionalFiltersHandler = () =>
    this.setState({ additionalFiltersVisible: true });
  onCloseAdditionalFiltersHandler = () =>
    this.setState({ additionalFiltersVisible: false });
  onOpenMobileFiltersMenuHandler = () =>
    this.setState({ mobileFiltersMenuVisible: true });
  onCloseMobileFiltersMenuHandler = () =>
    this.setState({ mobileFiltersMenuVisible: false });

  render() {
    const {
      filters,
      categoriesFilter,
      mobileFiltersMenuVisible,
      additionalFiltersVisible,
    } = this.state;
    const { expanded, moved } = this.props;
    const { t } = this.props.i18n;

    const additionalFiltersCounter =
      (filters?.clientTypes?.length ? 1 : 0) +
      (filters?.typeOfSalons?.length ? 1 : 0) +
      (filters?.facilitateAccess?.length ? 1 : 0);

    const filtersCounter =
      (filters?.payment?.length ? 1 : 0) +
      (filters?.genders?.length ? 1 : 0) +
      (filters?.accessToTheCustomer?.length ? 1 : 0) +
      (filters.price ? 1 : 0) +
      (categoriesFilter.mainCategories.length ? 1 : 0) +
      additionalFiltersCounter;

    const additionalFiltersBtnContent = additionalFiltersCounter ? (
      <div className={classes.additionalfiltersBtnContent}>
        <span className={classes.additionalfiltersBtnText}>
          {t("common:additional_filters")}
        </span>
        <div className={classes.counter}>{additionalFiltersCounter}</div>
      </div>
    ) : (
      <span className={classes.additionalfiltersBtnText}>
        {t("common:additional_filters")}
      </span>
    );

    const filtersMobileBtnContent = (
      <div className={classes.filtersMobileBtnContent}>
        <span>{t("common:filters")}</span>
        {!!filtersCounter && (
          <div className={classes.counter}>{filtersCounter}</div>
        )}
        {!filtersCounter && (
          <span className={classes.filterIcon}>
            <Image
              src={FilterIcon}
              width="24"
              height="24"
              alt={t("common:filters")}
            />
          </span>
        )}
      </div>
    );

    return (
      <>
        <div
          className={classNames(classes.filtersMobileBtnContainer, {
            [classes.expanded]: expanded,
          })}
        >
          <FilledButton
            type="button"
            onClick={this.onOpenMobileFiltersMenuHandler}
            content={filtersMobileBtnContent}
            className={classes.filtersMobileBtn}
          />
        </div>

        {mobileFiltersMenuVisible ? (
          <MobileFiltersMenu
            filterValues={filters}
            categoriesFilter={categoriesFilter}
            changeCategorySubFilterHandler={this.categorySubFilterChangeHandler}
            checkboxFilterValueChangeHandler={
              this.checkboxFilterValueChangeHandler
            }
            radioButtonFilterValueChangeHandler={
              this.radioButtonFilterValueChangeHandler
            }
            changeCategoryMainFilterHandler={
              this.categoryMainFilterChangeHandler
            }
            changePriceFilterHandler={this.priceFilterValueChangeHandler}
            clearFiltersHandler={this.clearMobileFiltersHandler}
            closeMenuHandler={this.onCloseMobileFiltersMenuHandler}
            filtersCounter={filtersCounter}
            submissionFilterHandler={this.submissionMobileFiltersHandler}
          />
        ) : (
          <div
            className={classNames(classes.filters, classes.desktop, {
              [classes.expanded]: expanded,
              [classes.moved]: moved,
            })}
          >
            <form>
              <ul>
                <li className={classNames(classes.input, classes.priceInput)}>
                  <PriceFilter
                    selectedValue={filters.price as string[]}
                    onChangeHandler={this.priceFilterValueChangeHandler}
                    placeholder={t("common:price")}
                    filterName="price"
                    onClearHandler={this.clearFilterValuesHandler}
                    onSubmitHandler={this.submissionFiltersHandler}
                  />
                </li>
                <li className={classes.input}>
                  <Filter
                    filterType="checkbox"
                    selectedOptions={filters.payment as string[]}
                    onChangeHandler={this.checkboxFilterValueChangeHandler}
                    options={paymentTypeOptions}
                    placeholder={t("common:type_of_payment")}
                    filterName="payment"
                    onClearHandler={this.clearFilterValuesHandler}
                    onSubmitHandler={this.submissionFiltersHandler}
                  />
                </li>
                <li className={classes.input}>
                  <Filter
                    filterType="checkbox"
                    selectedOptions={filters.accessToTheCustomer as string[]}
                    onChangeHandler={this.checkboxFilterValueChangeHandler}
                    options={accessToTheCustomerOptions}
                    placeholder={t("common:drive_to_customer")}
                    filterName="accessToTheCustomer"
                    onClearHandler={this.clearFilterValuesHandler}
                    onSubmitHandler={this.submissionFiltersHandler}
                  />
                </li>
                <li className={classNames(classes.input, classes.gendersInput)}>
                  <Filter
                    filterType="checkbox"
                    selectedOptions={filters.genders as string[]}
                    onChangeHandler={this.checkboxFilterValueChangeHandler}
                    options={genderOptions}
                    placeholder={t("common:sex_of_person_massaging")}
                    filterName="genders"
                    onClearHandler={this.clearFilterValuesHandler}
                    onSubmitHandler={this.submissionFiltersHandler}
                  />
                </li>
                <li>
                  <TextButton
                    type="button"
                    content={additionalFiltersBtnContent}
                    onClick={this.onOpenAdditionalFiltersHandler}
                    className={classes.additionalfiltersBtn}
                  />
                </li>
              </ul>
              <AdditionalFiltersModal
                filterValues={filters}
                filterValueChangeHandler={this.checkboxFilterValueChangeHandler}
                opened={additionalFiltersVisible}
                onApplyFiltersValues={this.submissionFiltersHandler}
                onClearFiltersValues={this.clearAdditionalFiltersHanlder}
                onClose={this.onCloseAdditionalFiltersHandler}
              />
            </form>
          </div>
        )}
      </>
    );
  }
}

export default withRouter(withTranslation(Filters));
